import React, {useCallback, useEffect, useState} from 'react';
import {
  Button,
  ButtonGroup,
  Heading,
  Popup,
  PopupSection
} from '@flixbus/honeycomb-react';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import {useAppDispatch} from '@/Pages/Common/hooks/useAppDispatch';
import {resetInvalidCostFactorsErrors} from '@/Pages/ProductionCost/store/slice';
import {
  isCountryRulesErrorCode,
  isRegionalOrBusPartnerErrorCode,
  isVehicleTypeErrorCode
} from './InvalidCostFactorsPopup.utils';
import BusPartnerErrorsSection from './BusPartnerErrorsSection/BusPartnerErrorsSection';
import CountryRulesErrorsSection from './CountryRulesErrorsSection/CountryRulesErrorsSection';
import VehicleTypeErrorsSection from './VehicleTypeErrorsSection/VehicleTypeErrorsSection';

const POPUP_CLOSE_ANIMATION_MS = 200;

const InvalidCostFactorsPopup: React.FC = () => {
  const busPartners = useAppSelector(({partners}) => partners.list);
  const countries = useAppSelector(({countries}) => countries.list);
  const vehicleTypeCostEntries = useAppSelector(
    ({productionCost}) => productionCost.vehicleTypeCostEntries
  );
  const busValues = useAppSelector(
    ({productionCost}) => productionCost.busValues
  );
  const costYear = useAppSelector(
    ({productionCost}) => productionCost.costYear
  );
  const invalidCostFactors = useAppSelector(
    ({productionCost}) =>
      productionCost.serverErrors.calculateProductionCost?.invalidCostFactors
  );

  const dispatch = useAppDispatch();

  const hasErrors = (invalidCostFactors?.length ?? 0) > 0;

  // Local popup lifecycle state (decoupled from Redux to avoid layout flicker during close animation)
  const [isOpen, setIsOpen] = useState(false);

  // Open popup when new errors appear
  useEffect(() => {
    if (hasErrors) {
      setIsOpen(true);
    }
  }, [hasErrors]);

  // Log unknown error codes
  useEffect(() => {
    (invalidCostFactors ?? [])
      .filter(
        ({errorCode}) =>
          !isVehicleTypeErrorCode(errorCode) &&
          !isRegionalOrBusPartnerErrorCode(errorCode) &&
          !isCountryRulesErrorCode(errorCode)
      )
      .forEach(({errorCode}) => {
        console.error(
          `Unknown error code for the input cost factors: ${errorCode}`
        );
      });
  }, [invalidCostFactors]);

  // Close popup first, then cleanup Redux after animation
  const closePopup = useCallback(() => {
    setIsOpen(false);

    setTimeout(() => {
      dispatch(resetInvalidCostFactorsErrors());
    }, POPUP_CLOSE_ANIMATION_MS);
  }, [dispatch]);

  return (
    <Popup
      aria-labelledby="invalid-cost-factors-popup"
      active={isOpen}
      extraClasses="invalid-cost-factors-popup"
    >
      <PopupSection type="title">
        <Heading size={1}>Missing data in cost factors database</Heading>
      </PopupSection>

      <PopupSection type="content">
        <BusPartnerErrorsSection
          invalidCostFactors={invalidCostFactors}
          busValues={busValues}
          busPartners={busPartners}
          countries={countries}
          costYear={costYear}
        />

        <CountryRulesErrorsSection
          invalidCostFactors={invalidCostFactors}
          busValues={busValues}
          busPartners={busPartners}
          countries={countries}
          costYear={costYear}
        />

        <VehicleTypeErrorsSection
          invalidCostFactors={invalidCostFactors}
          busValues={busValues}
          busPartners={busPartners}
          countries={countries}
          costYear={costYear}
          vehicleTypeCostEntries={vehicleTypeCostEntries}
        />
      </PopupSection>

      <PopupSection type="actions">
        <ButtonGroup alignment="center">
          <Button onClick={closePopup} key="closePopup">
            Close and try again
          </Button>
        </ButtonGroup>
      </PopupSection>
    </Popup>
  );
};

export default React.memo(InvalidCostFactorsPopup);
